import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Field, BigButton } from "@/components/Field";
import { api, errMsg } from "@/lib/api";
import { ROLE_LABEL } from "@/config/modules";

const EMPTY = { name: "", email: "", phone: "", password: "", role: "cliente" };

export const UserFormDialog = ({ open, onOpenChange, editing, roles, onSaved }) => {
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) setForm(editing ? { ...EMPTY, ...editing, phone: editing.phone || "", password: "" } : { ...EMPTY, role: roles[0] });
  }, [open, editing, roles]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (editing) {
        const body = { name: form.name, phone: form.phone, role: form.role };
        if (form.password) body.password = form.password;
        await api.put(`/users/${editing.user_id}`, body);
      } else {
        await api.post("/users", form);
      }
      toast.success(editing ? "Usuário atualizado" : "Usuário criado");
      onOpenChange(false);
      onSaved();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg rounded-3xl max-h-[92vh] overflow-y-auto" data-testid="user-form-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">{editing ? "Editar usuário" : "Novo usuário"}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4 mt-2">
          <Field label="Nome" id="u-name" required value={form.name} onChange={set("name")} data-testid="user-form-name" />
          <Field label="E-mail" id="u-email" type="email" required disabled={!!editing} value={form.email} onChange={set("email")} data-testid="user-form-email" />
          <Field label="Telefone" id="u-phone" type="tel" value={form.phone} onChange={set("phone")} data-testid="user-form-phone" />
          <div className="space-y-2">
            <Label className="text-sm font-semibold">Perfil de acesso</Label>
            <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
              <SelectTrigger className="h-14 rounded-xl text-base" data-testid="user-form-role"><SelectValue /></SelectTrigger>
              <SelectContent>
                {["admin", "tecnico", "cliente"].map((r) => (
                  <SelectItem key={r} value={r} className="h-12" data-testid={`user-form-role-${r}`}>{ROLE_LABEL[r]}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Field label={editing ? "Nova senha (opcional)" : "Senha inicial"} id="u-pass" type="password" minLength={6}
            required={!editing} value={form.password} onChange={set("password")} data-testid="user-form-password" />
          <BigButton type="submit" disabled={busy} data-testid="user-form-submit">
            {busy && <Loader2 className="h-5 w-5 animate-spin" />} Salvar
          </BigButton>
        </form>
      </DialogContent>
    </Dialog>
  );
};
